<script setup lang="ts">
import { AlertTriangle } from '@lucide/vue';
import Modal from './Modal.vue';
import Button from './Button.vue';

withDefaults(
  defineProps<{
    open: boolean;
    title?: string;
    message?: string;
    confirmLabel?: string;
    cancelLabel?: string;
    variant?: 'primary' | 'destructive';
    loading?: boolean;
  }>(),
  { variant: 'destructive', loading: false },
);

const emit = defineEmits<{ (e: 'update:open', v: boolean): void; (e: 'confirm'): void; (e: 'cancel'): void }>();

function onCancel(): void {
  emit('cancel');
  emit('update:open', false);
}

function onConfirm(): void {
  emit('confirm');
}
</script>

<template>
  <Modal :open="open" size="sm" :title="null" z-index-class="z-[80]" @update:open="(v) => emit('update:open', v)">
    <div class="space-y-4">
      <div class="flex items-start gap-3">
        <div :class="['grid h-10 w-10 shrink-0 place-items-center rounded-full', variant === 'destructive' ? 'bg-destructive/10 text-destructive' : 'bg-muted text-foreground']">
          <AlertTriangle class="h-5 w-5" />
        </div>
        <div class="min-w-0">
          <h3 v-if="title" class="text-base font-semibold">{{ title }}</h3>
          <p v-if="message" class="mt-1 text-sm text-muted-foreground">{{ message }}</p>
        </div>
      </div>
    </div>
    <template #footer>
      <Button variant="outline" :disabled="loading" @click="onCancel">{{ cancelLabel ?? 'Cancel' }}</Button>
      <Button :variant="variant === 'destructive' ? 'destructive' : 'primary'" :loading="loading" @click="onConfirm">
        {{ confirmLabel ?? 'Confirm' }}
      </Button>
    </template>
  </Modal>
</template>
